import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { api } from "../../api";
import { GameOrderItem } from "../../../types/admin";
import { ModalState } from "../../../types/modals";

export type GameOrderModalState = ModalState & {
    games: GameOrderItem[];
};

const initialState: GameOrderModalState = {
    loading: false,
    isOpen: false,
    error: null,
    games: [],
};

export const getGameOrder = createAsyncThunk(
    'gameOrderModal/getGameOrder',
    async (_, thunkAPI) => {
        try {
            return await api.get('/API/admin/games/order/');
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const updateGameOrder = createAsyncThunk(
    'gameOrderModal/updateGameOrder',
    async (payload: { games: GameOrderItem[] }, thunkAPI) => {
        try {
            const gameIds = payload.games.map((game) => game.game_id);
            return await api.put('/API/admin/games/order/', { games: gameIds });
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

const slice = createSlice({
    name: 'gameOrderModal',
    initialState,
    reducers: {
        openGameOrderModal: (state) => {
            state.isOpen = true;
        },
        closeGameOrderModal: () => {
            return initialState;
        },
        setGameOrder: (state, action: PayloadAction<GameOrderItem[]>) => {
            state.games = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getGameOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getGameOrder.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.games = action.payload.games || [];
            })
            .addCase(getGameOrder.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(updateGameOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateGameOrder.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.games = action.payload.games || [];
            })
            .addCase(updateGameOrder.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            });
    }
});

export const {
    openGameOrderModal,
    closeGameOrderModal,
    setGameOrder,
} = slice.actions;

export default slice.reducer;
